import { create } from 'zustand'
import type { ResultsTreeStateSnapshot } from '../../eval/eval-interface/results-tree'

/**
 * Dedicated store for Vibe Agent evaluation snapshots. The eval subsystem is
 * shared with the Truman eval interface, but its chat-side surface (the
 * per-run "Simulation results" cards) is not vibe chat state — it lives here.
 * The event bridge writes evalMessages; the renderer/panel read them.
 */

export type EvalSnapshot = ResultsTreeStateSnapshot

// Mirror backend eval stage.
export enum EvalMessageStatus {
  Started = 'started',
  Running = 'running',
  Completed = 'completed',
  Error = 'error',
  Cancelled = 'cancelled',
}

/** Minimal per-run eval card: what the bridge builds and the renderer reads. */
export interface EvalChatMessage {
  id: string
  evalId: string
  evalRunId?: number
  evalStatus?: EvalMessageStatus
  evalSnapshot?: EvalSnapshot
}

interface EvalStoreState {
  evalMessages: EvalChatMessage[]
}

interface EvalStoreActions {
  addEvalMessage: (message: EvalChatMessage) => void
  setEvalMessages: (messages: EvalChatMessage[]) => void
  persistEvalSnapshot: (evalId: string, runId: number | undefined, snapshot: EvalSnapshot) => void
  reset: () => void
}

export const useVibeEvalStore = create<EvalStoreState & EvalStoreActions>((set) => ({
  evalMessages: [],

  addEvalMessage: (message) => set((state) => ({ evalMessages: [...state.evalMessages, message] })),

  setEvalMessages: (messages) => set({ evalMessages: messages }),

  persistEvalSnapshot: (evalId, runId, snapshot) =>
    set((state) => {
      // With a run id, match it exactly. Without one, several cards of the
      // same eval can all have evalRunId undefined — write only the LATEST
      // card so one snapshot never smears across unrelated runs.
      const target =
        runId !== undefined
          ? state.evalMessages.findLast((m) => m.evalId === evalId && m.evalRunId === runId)
          : state.evalMessages.findLast((m) => m.evalId === evalId)
      if (!target) return state
      return {
        evalMessages: state.evalMessages.map((msg) =>
          msg.id === target.id ? { ...msg, evalSnapshot: snapshot } : msg,
        ),
      }
    }),

  reset: () => set({ evalMessages: [] }),
}))
